<script setup lang="ts">

import {DialogContent, DialogDescription, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import BasePhoneInput from "@/core/components/base/BasePhoneInput.vue";
import {Button} from "@/core/components/ui/button";
import {computed, ref} from "vue";
import Customer from "@/customer/interfaces/Customer";
import formatHelper from "@/core/helpers/formatHelper";
import {customerCertificationApi} from "@/container";
import {useRouter} from "vue-router";

const props = defineProps<{
    customer: Customer;
    onCertified?: () => void;
}>();

const router = useRouter();
const phone = ref('');
const loadingCertify = ref(false);

const error = ref<string | null>(null);

const certifyPhone = (phone: string) => {
    if (!formatHelper.phoneIsValid(phone)) {
        error.value = 'Le numéro doit correspondre au format 06 XX XX XX XX ou 07 XX XX XX XX';
        return;
    } else {
        error.value = null;
    }

    loadingCertify.value = true;
    customerCertificationApi()
        .certifyPhone(props.customer.id, formatHelper.apiPhone(phone))
        .then(async () => {
            if (props.onCertified) {
                return props.onCertified();
            }
        })
        .catch((err) => {
            if (err.status === 409) {
                error.value = 'Le numéro de téléphone est déjà certifié.';
            } else {
                error.value = 'Une erreur est survenue lors de la certification du téléphone.';
            }
        })
        .finally(() => {
            loadingCertify.value = false;
        });
};

const usePhone = () => {
    if (props.customer.information?.phone) {
        phone.value = formatHelper.readablePhone(props.customer.information.phone);
    }
};

const formattedCustomerPhone = computed(() => {
    if (!props.customer.information?.phone) {
        return null;
    }

    return formatHelper.readablePhone(props.customer.information.phone);
});

const navigateToPatientApp = () => {
    router.push({
        name: 'patient-administration.application'
    });
};
</script>

<template>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>
        Certification du patient
      </DialogTitle>
      <DialogDescription class="text-start">
        Veuillez certifier le numéro du patient avant d’envoyer un SMS
      </DialogDescription>
    </DialogHeader>
    <div>
      <div class="flex flex-col gap-6">
        <div>
          <div>
            <BasePhoneInput
              class="mx-auto"
              :input="phone"
              placeholder="06 __ __ __ __"
              format="phone"
              action="Certifier"
              :loading="loadingCertify"
              @submit="certifyPhone"
            />
            <p>
              <span
                v-if="error"
                class="text-sm text-red-500"
              >
                {{ error }}
              </span>
            </p>
          </div>
          <div
            v-if="customer.information.phone"
            class="flex flex-col"
          >
            <p class="text-sm">
              Numéro enregistré dans votre base : {{ formattedCustomerPhone }}
            </p>
            <p
              class="text-sm text-primary underline cursor-pointer"
              @click="usePhone"
            >
              Utiliser ce numéro
            </p>
          </div>
        </div>
        <div class="bg-orange rounded-lg p-3 text-primary flex flex-col gap-2">
          <div class="flex flex-row items-start gap-2">
            <img src="@/assets/images/iphone-mockup.png" class="w-16 h-auto object-contain" alt="patient mockup">
            <div class="flex flex-col items-center">
              <p>
                En certifiant le numéro du patient, votre patient pourra accéder à ses données depuis l’application
                mobile Apodis : ordonnances, suivi vaccinal, click & collect et plus encore à découvrir ici
              </p>
              <Button variant="link" class="text-primary" @click="navigateToPatientApp">
                Découvrir l'app Patient
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DialogContent>
</template>

<style scoped>

</style>
